import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { AlertsBanner } from '../components/NewsBanner';
import { 
  Bus, 
  Building2,
  Sun,
  Cloud,
  CloudRain,
  CloudSun,
  Globe,
  Heart,
  Landmark,
  MapPin,
  Hotel,
  Trees,
  UtensilsCrossed,
  Users,
  Accessibility,
  Dumbbell,
  Waves
} from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Tenerife coordinates
const TENERIFE_LAT = 28.4636;
const TENERIFE_LON = -16.2518;

// All categories for the home page (4 columns x 3 rows)
const CATEGORIES = [
  // Row 1
  { key: 'transport', path: '/transport', icon: Bus, color: 'bg-[#00599c]' },
  { key: 'health', path: '/services', icon: Heart, color: 'bg-red-500' },
  { key: 'pois', path: '/tourism', icon: MapPin, color: 'bg-emerald-500' },
  { key: 'nature', path: '/nature', icon: Trees, color: 'bg-green-600' },
  // Row 2
  { key: 'sports', path: '/sports', icon: Dumbbell, color: 'bg-lime-600' },
  { key: 'culture', path: '/culture', icon: Landmark, color: 'bg-purple-500' },
  { key: 'hospitality', path: '/hospitality', icon: UtensilsCrossed, color: 'bg-orange-500' },
  { key: 'accommodations', path: '/accommodations', icon: Hotel, color: 'bg-amber-500' },
  // Row 3
  { key: 'associations', path: '/associations', icon: Users, color: 'bg-indigo-500' },
  { key: 'accessibility', path: '/accessibility', icon: Accessibility, color: 'bg-blue-500' },
  { key: 'beaches', path: '/beaches', icon: Waves, color: 'bg-cyan-500' },
  { key: 'publicServices', path: '/public-services', icon: Building2, color: 'bg-slate-500' },
];

// Stats to display (6 stats in order)
const STAT_KEYS = ['transport_stops', 'health_centers', 'points_of_interest', 'trails', 'hospitality', 'accommodations'];

// Stats to URL mapping
const STATS_URLS = {
  transport_stops: '/transport',
  trails: '/nature',
  hospitality: '/hospitality',
  accommodations: '/accommodations',
  points_of_interest: '/tourism',
  culture: '/culture',
  health_centers: '/services',
};

// Category labels
const CATEGORY_LABELS = {
  es: {
    transport: 'Transporte',
    nature: 'Naturaleza',
    hospitality: 'Hostelería',
    accommodations: 'Alojam.',
    pois: 'Puntos de Interés',
    culture: 'Cultura y Educación',
    sports: 'Deportes',
    health: 'Atención Sanitaria',
    associations: 'Asoc.',
    accessibility: 'Accesibilidad',
    beaches: 'Playas',
    publicServices: 'Trámites',
  },
  en: {
    transport: 'Transport',
    nature: 'Nature',
    hospitality: 'Hospitality',
    accommodations: 'Lodging',
    pois: 'Points of Interest',
    culture: 'Culture & Education',
    sports: 'Sports',
    health: 'Healthcare',
    associations: 'Assoc.',
    accessibility: 'Accessibility',
    beaches: 'Beaches',
    publicServices: 'Procedures',
  },
};

// Stats labels
const STATS_LABELS = {
  es: {
    transport_stops: 'Paradas de transporte',
    trails: 'Senderos',
    hospitality: 'Establecimientos de hostelería',
    accommodations: 'Alojamientos',
    points_of_interest: 'Puntos de interés',
    culture: 'Cultura',
    health_centers: 'Centros Sanitarios',
  },
  en: {
    transport_stops: 'Transport Stops',
    trails: 'Trails',
    hospitality: 'Hospitality Establishments',
    accommodations: 'Accommodations',
    points_of_interest: 'Points of Interest',
    culture: 'Culture',
    health_centers: 'Health Centers',
  },
};

// Weather code to icon mapping
const getWeatherIcon = (code) => {
  if (code === 0) return Sun;
  if (code <= 3) return CloudSun;
  if (code >= 51 && code <= 67) return CloudRain;
  if (code >= 80 && code <= 82) return CloudRain;
  return Cloud;
};

export function HomePage() {
  const { language, toggleLanguage } = useLanguage();
  const [stats, setStats] = useState(null);
  const [weather, setWeather] = useState(null);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const response = await axios.get(`${API}/stats`);
        if (response.data) {
          setStats(response.data);
        }
      } catch (error) {
        console.error('Error fetching stats:', error);
      }
    };
    fetchStats();
  }, []);

  useEffect(() => {
    const fetchWeather = async () => {
      try {
        const response = await axios.get(
          `https://api.open-meteo.com/v1/forecast?latitude=${TENERIFE_LAT}&longitude=${TENERIFE_LON}&current=temperature_2m,weather_code&daily=temperature_2m_max&timezone=Atlantic/Canary`
        );
        const data = response.data;
        setWeather({
          current: Math.round(data.current.temperature_2m),
          weatherCode: data.current.weather_code,
          tomorrow: Math.round(data.daily.temperature_2m_max[1]),
        });
      } catch (error) {
        console.error('Error fetching weather:', error);
        setWeather({ current: 22, weatherCode: 0, tomorrow: 23 });
      }
    };
    fetchWeather();
  }, []);

  const categoryLabels = CATEGORY_LABELS[language];
  const statsLabels = STATS_LABELS[language];

  return (
    <div className="min-h-screen bg-white">
      {/* Header Banner */}
      <div className="bg-[#00599c] text-white">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-white rounded-lg flex items-center justify-center">
                <span className="text-[#00599c] font-black text-2xl font-['Outfit']">T</span>
              </div>
              <div>
                <h1 className="text-xl font-bold font-['Outfit']" data-testid="home-title">
                  TENERIFE CERCA
                </h1>
                <p className="text-sm text-white/80 font-['Outfit']">
                  {language === 'es' ? 'Información útil, basada en datos abiertos' : 'Useful information, based on open data'}
                </p>
              </div>
            </div>
            {/* Weather widget & Language toggle */}
            <div className="flex items-center gap-4 text-sm">
              {weather && (
                <>
                  <div className="flex items-center gap-2">
                    {(() => {
                      const WeatherIcon = getWeatherIcon(weather.weatherCode);
                      return <WeatherIcon className="w-5 h-5" />;
                    })()}
                    <span className="font-medium">{weather.current}°C</span>
                  </div>
                  <div className="hidden sm:flex items-center gap-2 text-white/70">
                    <Cloud className="w-4 h-4" />
                    <span>{language === 'es' ? 'Mañana' : 'Tomorrow'}: {weather.tomorrow}°C</span>
                  </div>
                </>
              )}
              <Button
                variant="ghost"
                size="sm"
                onClick={toggleLanguage}
                data-testid="language-toggle-header"
                aria-label={language === 'es' ? 'Cambiar a inglés' : 'Switch to Spanish'}
                className="hidden md:flex items-center gap-2 text-white hover:bg-white/10 focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-[#00599c]"
              >
                <Globe size={16} aria-hidden="true" />
                <span className="font-medium uppercase text-sm">{language}</span>
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Alerts Banner */}
      <AlertsBanner />

      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Categories Grid - 3 columns x 4 rows */}
        <div className="grid grid-cols-3 sm:landscape:grid-cols-4 gap-2 sm:gap-6 mb-8">
          {CATEGORIES.map((category) => {
            const CategoryIcon = category.icon;
            const isComingSoon = category.comingSoon;
            
            const content = (
              <div className="flex flex-col items-center group">
                <div className={`w-16 h-16 sm:w-24 sm:h-24 rounded-full ${category.color} flex items-center justify-center shadow-lg ${!isComingSoon ? 'group-hover:scale-105' : 'opacity-60'} transition-transform duration-200`}>
                  <CategoryIcon className="w-8 h-8 sm:w-12 sm:h-12 text-white" strokeWidth={1.5} />
                </div>
                <span className="mt-2 text-[11px] sm:text-base font-medium text-[#252a34] text-center leading-tight px-1">
                  {categoryLabels[category.key]}
                </span>
                {isComingSoon && (
                  <span className="mt-1 text-xs text-slate-500 bg-slate-100 px-2 py-0.5 rounded-full">
                    {categoryLabels.comingSoon}
                  </span>
                )}
              </div>
            );

            if (isComingSoon || !category.path) {
              return (
                <div key={category.key} className="flex flex-col items-center cursor-not-allowed">
                  {content}
                </div>
              );
            }

            return (
              <Link
                key={category.key}
                to={category.path}
                data-testid={`category-${category.key}`}
                aria-label={`${categoryLabels[category.key]}. ${language === 'es' ? 'Ir a la sección de' : 'Go to'} ${categoryLabels[category.key]}`}
                role="link"
                className="focus:outline-none focus:ring-2 focus:ring-[#00599c] focus:ring-offset-2 rounded-xl"
              >
                {content}
              </Link>
            );
          })}
        </div>

        {/* Quick Stats */}
        {stats && (
          <Card className="border-slate-200 bg-[#f8f9fa]">
            <CardContent className="p-4">
              <div className="grid grid-cols-3 sm:grid-cols-6 gap-3 sm:gap-2 text-center">
                {STAT_KEYS.map((statKey) => (
                  <Link 
                    key={statKey} 
                    to={STATS_URLS[statKey] || '/'}
                    className="p-2 rounded-lg hover:bg-white transition-colors cursor-pointer"
                  >
                    <p className="text-lg sm:text-2xl font-bold text-[#00599c] font-['Outfit']">
                      {(stats[statKey] || 0).toLocaleString()}
                    </p>
                    <p className="text-[10px] sm:text-xs text-[#252a34]/60 uppercase hover:text-[#00599c] leading-tight">
                      {statsLabels[statKey]}
                    </p>
                  </Link>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* About Link */}
        <div className="mt-6 text-center">
          <Link 
            to="/about" 
            className="text-sm text-[#252a34]/50 hover:text-[#00599c] transition-colors"
          >
            {language === 'es' ? 'Acerca de este proyecto' : 'About this project'} →
          </Link>
        </div>
      </div>
    </div>
  );
}
